import React, {SyntheticEvent, useMemo, useState} from 'react';
import {AvailableCoupon, CustomerDashboardSnippetProfile, ISyntheticEvent} from "../Helpers/Types";
import QRCode from "react-qr-code";
import ClickToRedeemWarning from "./ClickToRedeemWarning";
import { ImpulseSpinner } from 'react-spinners-kit';

type DashboardCouponDisplayProps = {
    coupon: AvailableCoupon
    apply: (code: string, couponUid?: string) => void
    onCompleteExists: boolean
    config?: CustomerDashboardSnippetProfile
    isCartMode?: boolean
    applyStatus?: 'idle' | 'loading' | 'success' | 'error'
}

export default function DashboardCouponDisplay({coupon, apply, config, onCompleteExists, isCartMode, applyStatus}: DashboardCouponDisplayProps) {
    const [flipped, setFlipped] = useState(false)
    const [showWarning, setShowWarning] = useState(false)
    const showCode = useMemo(() => config?.ShowCouponCode ?? false, [config])
    const showQR = useMemo(() => config?.ShowCouponQRCode ?? false, [config])
    const applyLabel = useMemo(() => config?.ApplyCouponLabel ?? false, [config])
    const clickToRedeem = useMemo(() => config?.EnableClickToRedeem ?? false, [config])
    const allowApply = useMemo(() => clickToRedeem || isCartMode, [onCompleteExists, clickToRedeem, isCartMode])
    
    const isLoading = isCartMode && applyStatus === 'loading';
    const isSuccess = isCartMode && applyStatus === 'success';
    const isError = isCartMode && applyStatus === 'error';
    
    const flip = (e: SyntheticEvent) => {
        e.preventDefault();
        e.stopPropagation(); // Prevent event bubbling to parent div
        if (isCartMode) {
            apply(coupon.Code, coupon.Uid)
            return
        }
        if (!clickToRedeem) {
            setFlipped(!flipped)
        } else {
            setShowWarning(true)
        }
    }
    
    const applyCoupon = (e: SyntheticEvent) => {
        e.preventDefault()
        e.stopPropagation() // Prevent event bubbling to parent div
        apply(coupon.Code, coupon.Uid)
    }
    
    const cancel = () => {
        setShowWarning(false)
    }
    
    const confirm = () => {
        apply(coupon.Code, coupon.Uid)
        setFlipped(!flipped)
        setShowWarning(false)
    }
    
    return (
        <>
            {showWarning && !isCartMode && <ClickToRedeemWarning cancel={cancel} confirm={confirm} title={config?.CouponConfirmationTitle ?? "Click-To-Redeem™"} body={config?.CouponConfirmationText ?? "This will mark the coupon as redeemed, only confirm if you are ready to use the coupon now"} />}
            <div className={`bl-dashboard-coupon bLoyal-center ${clickToRedeem ? '' : 'clickable'}`} onClick={clickToRedeem ? undefined : flip}>
                {!flipped &&
                    <>
                        <p className='bl-coupon-name bLoyal-center'>{coupon.Name}</p>
                        {showCode && <p className='bl-coupon-code bLoyal-center'>{coupon.Code}</p>}
                        {coupon.QuantityAvailable !== null &&
                            <>
                                <br />
                                <p>x{coupon.QuantityAvailable}</p>
                            </>
                        }
                        {allowApply && (
                            <div style={{display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '8px'}}>
                                <button 
                                    className='bl-snippet-button' 
                                    style={{
                                        padding: '4px 8px', 
                                        opacity: isLoading ? 0.6 : 1, 
                                        cursor: isLoading ? 'not-allowed' : 'pointer',
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: '8px',
                                        minWidth: '100px',
                                        justifyContent: 'center'
                                    }} 
                                    onClick={flip}
                                    disabled={isLoading}
                                >
                                    <span style={{display: 'inline-flex', alignItems: 'center', gap: '8px', minHeight: '16px'}}>
                                        <span style={{display: isLoading ? 'inline-flex' : 'none', alignItems: 'center', width: '16px', height: '16px', overflow: 'hidden'}}>
                                            <ImpulseSpinner loading={isLoading} size={16} frontColor="#a7a7a7" backColor="#000000" />
                                        </span>
                                        <span>{isLoading ? 'Applying...' : (applyLabel ? applyLabel : 'Apply')}</span>
                                    </span>
                                </button>
                                {isSuccess && !isLoading && (
                                    <p key="success" className='bLoyal-center' style={{color: '#28a745', fontSize: '14px', fontWeight: 'bold', margin: 0}}>Applied</p>
                                )}
                                {isError && !isLoading && (
                                    <p key="error" className='bLoyal-center' style={{color: '#dc3545', fontSize: '14px', fontWeight: 'bold', margin: 0}}>Failed</p>
                                )}
                            </div>
                        )}
                    </>
                }
                {flipped &&
                    <>
                        <p className='bl-coupon-name bLoyal-center'>{coupon.Name}</p>
                        {/*@ts-ignore*/}
                        {showQR && <div className='bLoyal-qr-code'><QRCode value={coupon.Code} size={100}/></div>}
                        {showCode && <p className='bl-coupon-code bLoyal-center' style={{marginTop: '8px'}}>{coupon.Code}</p>}
                        
                    </>
                }
            </div>
        </>
    )
}